import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Plus, Edit, Trash2, Eye, Package, Printer, ShoppingCart, Search, X, Image, Video, Link as LinkIcon, Play } from 'lucide-react';
import { FileUploader } from './FileUploader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminOrders = ({ orders, onRefresh }) => {
  const { token } = useAuth();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const { t } = useTranslation();

  const updateStatus = async (orderId, status) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/orders/${orderId}/status`, { status }, { headers });
      toast.success(t('admin.orderStatusUpdated'));
      onRefresh();
    } catch (err) {
      toast.error(t('admin.updateFailed'));
    }
  };

  const getStatusBadge = (status) => {
    const colorMap = {
      pending: 'bg-yellow-100 text-yellow-700',
      paid: 'bg-blue-100 text-blue-700',
      processing: 'bg-purple-100 text-purple-700',
      shipped: 'bg-indigo-100 text-indigo-700',
      delivered: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700',
    };
    return <Badge className={colorMap[status] || 'bg-amber-100 text-amber-700'}>{t(`admin.orderStatuses.${status}`, status)}</Badge>;
  };

  const printShippingLabel = (order) => {
    const printWindow = window.open('', '_blank');
    const printContent = `<!DOCTYPE html><html><head><title>${t('admin.printShipping')} - ${order.order_number}</title><style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Microsoft YaHei',Arial,sans-serif;padding:20px;max-width:800px;margin:0 auto}.header{text-align:center;border-bottom:2px solid #16a34a;padding-bottom:15px;margin-bottom:20px}.header h1{color:#16a34a;font-size:24px}.order-info{display:flex;justify-content:space-between;margin-bottom:20px;padding:10px;background:#f5f5f5;border-radius:5px}.section{margin-bottom:20px}.section-title{font-size:16px;font-weight:bold;color:#16a34a;border-left:4px solid #16a34a;padding-left:10px;margin-bottom:10px}.address-box{border:1px solid #ddd;padding:15px;border-radius:5px}table{width:100%;border-collapse:collapse;margin-top:10px}th,td{border:1px solid #ddd;padding:10px;text-align:left}.product-img{width:50px;height:50px;object-fit:cover;border-radius:5px}.total-row{background:#f0fdf4;font-weight:bold;color:#16a34a}.footer{margin-top:30px;padding-top:15px;border-top:1px dashed #ddd;display:flex;justify-content:space-between;font-size:12px;color:#999}@media print{.no-print{display:none}}</style></head><body><div class="header"><h1>🌿 ${t('siteName')}</h1><div>${t('admin.printShipping')}</div></div><div class="order-info"><div><strong>${t('admin.orderNumber')}:</strong>${order.order_number}</div><div><strong>${t('admin.orderTime')}:</strong>${new Date(order.created_at).toLocaleString()}</div><div><strong>${t('admin.paymentStatus')}:</strong>${order.payment_status === 'paid' ? '✅ ' + t('admin.paid') : '⏳ ' + t('admin.unpaid')}</div></div><div class="section"><div class="section-title">${t('admin.shippingInfo')}</div><div class="address-box"><div style="font-size:18px;font-weight:bold">📦 ${order.shipping_address?.name || ''}</div><div style="font-size:16px">📞 ${order.shipping_address?.phone || ''}</div><div style="font-size:14px;color:#666">📍 ${order.shipping_address?.province || ''} ${order.shipping_address?.city || ''} ${order.shipping_address?.district || ''} ${order.shipping_address?.address || ''}</div></div></div><div class="section"><div class="section-title">${t('admin.productInfo')}</div><table><thead><tr><th style="width:60px">${t('admin.image')}</th><th>${t('admin.name')}</th><th style="width:80px">${t('admin.unitPrice')}</th><th style="width:60px">${t('cart.quantity')}</th><th style="width:100px">${t('checkout.subtotal')}</th></tr></thead><tbody>${order.items?.map(item => `<tr><td><img src="${item.product_image}" class="product-img" onerror="this.src='https://via.placeholder.com/50'" /></td><td>${item.product_name}</td><td>¥${item.price?.toFixed(2)}</td><td>${item.quantity}</td><td>¥${item.subtotal?.toFixed(2)}</td></tr>`).join('')}<tr class="total-row"><td colspan="4" style="text-align:right">${t('admin.productTotal')}:</td><td>¥${order.total_amount?.toFixed(2)}</td></tr></tbody></table></div><div class="footer"><span>${new Date().toLocaleString()}</span><span>${t('siteName')}</span></div><div class="no-print" style="text-align:center;margin-top:30px"><button onclick="window.print()" style="padding:10px 30px;background:#16a34a;color:white;border:none;border-radius:5px;cursor:pointer">🖨️ Print</button><button onclick="window.close()" style="padding:10px 30px;background:#666;color:white;border:none;border-radius:5px;cursor:pointer;margin-left:10px">Close</button></div></body></html>`;
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  return (
    <div data-testid="admin-orders">
      <h1 className="text-2xl font-bold mb-8">{t('admin.orders')}</h1>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('admin.orderNumber')}</TableHead>
                <TableHead>{t('admin.customer')}</TableHead>
                <TableHead>{t('admin.products')}</TableHead>
                <TableHead>{t('admin.amount')}</TableHead>
                <TableHead>{t('admin.status')}</TableHead>
                <TableHead>{t('admin.time')}</TableHead>
                <TableHead>{t('admin.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((order) => (
                <TableRow key={order.id}>
                  <TableCell className="font-mono text-sm">{order.order_number}</TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{order.shipping_address.name}</p>
                      <p className="text-sm text-amber-600">{order.shipping_address.phone}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex -space-x-2">
                      {order.items.slice(0, 3).map((item) => (
                        <img key={item.product_id} src={item.product_image} alt={item.product_name} className="w-8 h-8 rounded-full border-2 border-white object-cover" />
                      ))}
                      {order.items.length > 3 && (
                        <div className="w-8 h-8 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center text-xs">+{order.items.length - 3}</div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">¥{(order.total_amount ?? 0).toFixed(2)}</TableCell>
                  <TableCell>{getStatusBadge(order.status)}</TableCell>
                  <TableCell className="text-sm text-amber-600">{new Date(order.created_at).toLocaleString()}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => { setSelectedOrder(order); setShowDetail(true); }} data-testid={`view-order-${order.id}`}>
                        <Eye className="w-4 h-4 mr-1" /> {t('admin.details')}
                      </Button>
                      <Select value={order.status} onValueChange={(v) => updateStatus(order.id, v)}>
                        <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">{t('admin.orderStatuses.pending')}</SelectItem>
                          <SelectItem value="paid">{t('admin.orderStatuses.paid')}</SelectItem>
                          <SelectItem value="processing">{t('admin.orderStatuses.processing')}</SelectItem>
                          <SelectItem value="shipped">{t('admin.orderStatuses.shipped')}</SelectItem>
                          <SelectItem value="delivered">{t('admin.orderStatuses.delivered')}</SelectItem>
                          <SelectItem value="cancelled">{t('admin.orderStatuses.cancelled')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {orders.length === 0 && (<div className="text-center py-12 text-amber-600">{t('admin.noOrders')}</div>)}
        </CardContent>
      </Card>

      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('admin.orderDetail')}</DialogTitle>
            <DialogDescription>{t('admin.orderNumber')}: {selectedOrder?.order_number}</DialogDescription>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-6">
              <div className="flex justify-end">
                <Button onClick={() => printShippingLabel(selectedOrder)} className="bg-green-600 hover:bg-green-700" data-testid="print-shipping-label-btn">
                  <Printer className="w-4 h-4 mr-2" /> {t('admin.printShipping')}
                </Button>
              </div>
              <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg">
                <div><p className="text-sm text-amber-600">{t('admin.orderStatus')}</p><div className="mt-1">{getStatusBadge(selectedOrder.status)}</div></div>
                <div><p className="text-sm text-amber-600">{t('admin.paymentStatus')}</p><Badge className={selectedOrder.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>{selectedOrder.payment_status === 'paid' ? t('admin.paid') : t('admin.unpaid')}</Badge></div>
                <div><p className="text-sm text-amber-600">{t('admin.orderTime')}</p><p className="font-medium">{new Date(selectedOrder.created_at).toLocaleString()}</p></div>
              </div>
              <Card>
                <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Package className="w-4 h-4" /> {t('admin.shippingInfo')}</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  <div className="grid grid-cols-2 gap-4">
                    <div><p className="text-sm text-amber-600">{t('admin.recipient')}</p><p className="font-medium">{selectedOrder.shipping_address?.name}</p></div>
                    <div><p className="text-sm text-amber-600">{t('admin.phone')}</p><p className="font-medium">{selectedOrder.shipping_address?.phone}</p></div>
                  </div>
                  <div><p className="text-sm text-amber-600">{t('admin.address')}</p><p className="font-medium">{selectedOrder.shipping_address?.province} {selectedOrder.shipping_address?.city} {selectedOrder.shipping_address?.district} {selectedOrder.shipping_address?.address}</p></div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><ShoppingCart className="w-4 h-4" /> {t('admin.productInfo')}</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {selectedOrder.items?.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-4 p-3 bg-amber-50 rounded-lg">
                        <img src={item.product_image} alt={item.product_name} className="w-16 h-16 object-cover rounded" onError={(e) => { e.target.src = 'https://via.placeholder.com/64?text=No+Image'; }} />
                        <div className="flex-1"><p className="font-medium">{item.product_name}</p><p className="text-sm text-amber-600">{t('admin.unitPrice')}: ¥{item.price?.toFixed(2)} × {item.quantity}</p></div>
                        <p className="font-bold text-green-600">¥{item.subtotal?.toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                  <Separator className="my-4" />
                  <div className="flex justify-between items-center"><span className="text-amber-600">{t('admin.productTotal')}</span><span className="text-xl font-bold text-green-600">¥{selectedOrder.total_amount?.toFixed(2)}</span></div>
                </CardContent>
              </Card>
              {selectedOrder.note && (<Card><CardHeader className="pb-3"><CardTitle className="text-base">{t('admin.orderNote')}</CardTitle></CardHeader><CardContent><p className="text-amber-700">{selectedOrder.note}</p></CardContent></Card>)}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};


export default AdminOrders;
